// Catalog.ID Client — Account Unlock Sheet scan → account id
// ============================================================================
// The reset ceremony needs the account id: it routes the request to the home
// node and supplies the Argon2id salt for the new authHash. It is public
// knowledge (printed on the sheet), not an auth factor — the sheet and the
// channel code are.
//
// Amendment 2026-09-03: sheets printed with the CIDA framing carry the id in
// the QR, so the flow reads it off the scan and asks for nothing. Only a
// legacy sheet (bare sealed envelope, no id) still needs it typed — and a typo
// there is invisible to the member, because the server answers a wrong id with
// the same anti-enumeration decoy as a right one. So the shape is checked HERE,
// before the request goes out.

/** A Catalog.ID: exactly 10 lowercase letters/digits (server CidService). */
const CID_PATTERN = /^[a-z0-9]{10}$/;

/** Only the part of a decoded sheet this decision needs. */
export interface ScannedAccessCard {
    cid: string | null;
}

export interface ResetCidDecision {
    /** The account id to send to reset/begin, when one could be determined. */
    cid?: string;
    /** Why nothing can be sent yet — shown verbatim to the member. */
    error?: string;
}

/**
 * Decide which account id the reset request should carry.
 *
 * @param card  the decoded Account Unlock Sheet, or null when none is scanned yet
 * @param typed whatever is in the Catalog.ID field (only consulted for a legacy
 *              sheet — a sheet that names its own account always wins)
 */
export function resolveResetCid(card: ScannedAccessCard | null, typed: string): ResetCidDecision {
    if (!card) {
        return { error: 'Scan or photograph your Account Unlock Sheet first' };
    }
    if (card.cid) {
        return { cid: card.cid };
    }
    const cid = typed.trim().toLowerCase();
    if (!cid) {
        return { error: 'Enter your Catalog.ID — it is printed on the Account Unlock Sheet' };
    }
    if (!CID_PATTERN.test(cid)) {
        return {
            error: 'That does not look like a Catalog.ID — it is 10 letters and digits, '
                + 'printed on the Account Unlock Sheet',
        };
    }
    return { cid };
}
